/**
 * 施工実績作成・編集ページ
 */

import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AdminClient, TypeUtils, ResultMedia } from "../../../api/generated/APIClient";
import { getImageUrl } from "../../../utils/imageFetcher";
import "./ResultFormPage.css";

interface ResultFormData {
  id?: string;
  title: string;
  categoryId: string;
  completionDate: string;
  mainImage: ResultMedia | null;
  galleryImages: ResultMedia[];
  videos: ResultMedia[];
  clientName: string;
  location: string;
  siteArea: string;
  buildingArea: string;
  totalFloorArea: string;
  structureType: string;
  description: string;
  clientInfo: {
    companyName: string;
    address: string;
    websiteUrl: string;
    description: string;
  };
  displayPriority: number;
  isPublished: boolean;
  createDate?: string;
  updateDate?: string;
  version?: string;
}

interface ResultFormPageProps {
  categories: any[];
}

export const ResultFormPage: React.FC<ResultFormPageProps> = ({
  categories,
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const mainImageInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<ResultFormData>({
    title: "",
    categoryId: "",
    completionDate: "",
    mainImage: null,
    galleryImages: [],
    videos: [],
    clientName: "",
    location: "",
    siteArea: "",
    buildingArea: "",
    totalFloorArea: "",
    structureType: "",
    description: "",
    clientInfo: {
      companyName: "",
      address: "",
      websiteUrl: "",
      description: "",
    },
    displayPriority: 0,
    isPublished: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingMainImage, setUploadingMainImage] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  // 編集モードの場合、データを取得
  useEffect(() => {
    if (isEditMode && id) {
      setIsLoading(true);
      const fetchResult = async () => {
        try {
          const response = await (AdminClient as any).IndexResult({
            offset: 0,
            limit: 0,
          });

          let items: any[] = [];
          if (response?.data) {
            if (Array.isArray(response.data)) {
              items = response.data;
            } else if (
              response.data.results &&
              Array.isArray(response.data.results)
            ) {
              items = response.data.results;
            } else if (
              response.data.items &&
              Array.isArray(response.data.items)
            ) {
              items = response.data.items;
            }
          }

          const result = items.find((item: any) => item.id === id);
          if (result) {
            // datetime-local用にフォーマット
            const formatDateLocal = (dateValue: any): string => {
              if (!dateValue) return "";
              const date = new Date(dateValue);
              if (isNaN(date.getTime())) return "";
              return date.toISOString().slice(0, 10);
            };

            const clientInfo = result.clientInfo || result.client_info || {};

            setFormData({
              id: result.id,
              title: result.title || "",
              categoryId: result.categoryId || result.category_id || "",
              completionDate: formatDateLocal(
                result.completionDate || result.completion_date
              ),
              mainImage: result.mainImage || result.main_image || null,
              galleryImages: result.galleryImages || result.gallery_images || [],
              videos: result.videos || [],
              clientName: result.clientName || result.client_name || "",
              location: result.location || "",
              siteArea: result.siteArea || result.site_area || "",
              buildingArea: result.buildingArea || result.building_area || "",
              totalFloorArea:
                result.totalFloorArea || result.total_floor_area || "",
              structureType:
                result.structureType || result.structure_type || "",
              description: result.description || "",
              clientInfo: {
                companyName:
                  clientInfo.companyName || clientInfo.company_name || "",
                address: clientInfo.address || "",
                websiteUrl: clientInfo.websiteUrl || clientInfo.website_url || "",
                description: clientInfo.description || "",
              },
              displayPriority:
                result.displayPriority ?? result.display_priority ?? 0,
              isPublished: result.isPublished ?? result.is_published ?? false,
              createDate: result.createDate || result.create_date,
              updateDate: result.updateDate || result.update_date,
              version: result.version,
            });
          }
        } catch (error) {
          console.error("施工実績の取得に失敗しました:", error);
          alert("施工実績の取得に失敗しました");
        } finally {
          setIsLoading(false);
        }
      };

      fetchResult();
    }
  }, [id, isEditMode]);

  const handleInputChange = (field: keyof ResultFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleClientInfoChange = (
    field: keyof ResultFormData["clientInfo"],
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      clientInfo: {
        ...prev.clientInfo,
        [field]: value,
      },
    }));
  };

  // ファイルをBase64に変換
  const fileToBase64 = (file: File): Promise<Uint8Array> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const arrayBuffer = reader.result as ArrayBuffer;
        resolve(new Uint8Array(arrayBuffer));
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  // メイン画像アップロード
  const handleMainImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const resultId = formData.id || crypto.randomUUID();
    if (!formData.id) {
      setFormData(prev => ({ ...prev, id: resultId }));
    }

    setUploadingMainImage(true);
    try {
      const fileData = await fileToBase64(file);
      const response = await AdminClient.UploadResultMedia({
        fileData: fileData,
        fileName: file.name,
        contentType: file.type,
        resultId: resultId,
        mediaType: "image",
        caption: "",
      });

      if (response.data?.media) {
        setFormData(prev => ({
          ...prev,
          id: resultId,
          mainImage: response.data!.media as ResultMedia,
        }));
      }
    } catch (error) {
      console.error("メイン画像のアップロードに失敗しました:", error);
      alert("メイン画像のアップロードに失敗しました");
    } finally {
      setUploadingMainImage(false);
      if (mainImageInputRef.current) {
        mainImageInputRef.current.value = "";
      }
    }
  };

  // メイン画像削除
  const handleDeleteMainImage = async () => {
    if (!formData.mainImage?.storagePath) return;

    try {
      await AdminClient.DeleteResultMedia({
        storagePath: formData.mainImage.storagePath,
      });
      setFormData(prev => ({ ...prev, mainImage: null }));
    } catch (error) {
      console.error("メイン画像の削除に失敗しました:", error);
      alert("メイン画像の削除に失敗しました");
    }
  };

  // ギャラリー画像アップロード
  const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const resultId = formData.id || crypto.randomUUID();
    if (!formData.id) {
      setFormData(prev => ({ ...prev, id: resultId }));
    }

    setUploadingGallery(true);
    try {
      const uploadedMedia: ResultMedia[] = [];

      for (const file of Array.from(files)) {
        const fileData = await fileToBase64(file);
        const response = await AdminClient.UploadResultMedia({
          fileData: fileData,
          fileName: file.name,
          contentType: file.type,
          resultId: resultId,
          mediaType: "image",
          caption: "",
        });

        if (response.data?.media) {
          uploadedMedia.push(response.data.media as ResultMedia);
        }
      }

      if (uploadedMedia.length > 0) {
        setFormData(prev => ({
          ...prev,
          id: resultId,
          galleryImages: [...prev.galleryImages, ...uploadedMedia],
        }));
      }
    } catch (error) {
      console.error("ギャラリー画像のアップロードに失敗しました:", error);
      alert("ギャラリー画像のアップロードに失敗しました");
    } finally {
      setUploadingGallery(false);
      if (galleryInputRef.current) {
        galleryInputRef.current.value = "";
      }
    }
  };

  // ギャラリー画像削除
  const handleDeleteGalleryImage = async (index: number) => {
    const image = formData.galleryImages[index];
    if (!image?.storagePath) return;

    try {
      await AdminClient.DeleteResultMedia({
        storagePath: image.storagePath,
      });
      setFormData(prev => ({
        ...prev,
        galleryImages: prev.galleryImages.filter((_, i) => i !== index),
      }));
    } catch (error) {
      console.error("ギャラリー画像の削除に失敗しました:", error);
      alert("ギャラリー画像の削除に失敗しました");
    }
  };

  // 動画アップロード
  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const resultId = formData.id || crypto.randomUUID();
    if (!formData.id) {
      setFormData(prev => ({ ...prev, id: resultId }));
    }

    setUploadingVideo(true);
    try {
      const uploadedMedia: ResultMedia[] = [];

      for (const file of Array.from(files)) {
        const fileData = await fileToBase64(file);
        const response = await AdminClient.UploadResultMedia({
          fileData: fileData,
          fileName: file.name,
          contentType: file.type,
          resultId: resultId,
          mediaType: "video",
          caption: "",
        });

        if (response.data?.media) {
          uploadedMedia.push(response.data.media as ResultMedia);
        }
      }

      if (uploadedMedia.length > 0) {
        setFormData(prev => ({
          ...prev,
          id: resultId,
          videos: [...prev.videos, ...uploadedMedia],
        }));
      }
    } catch (error) {
      console.error("動画のアップロードに失敗しました:", error);
      alert("動画のアップロードに失敗しました");
    } finally {
      setUploadingVideo(false);
      if (videoInputRef.current) {
        videoInputRef.current.value = "";
      }
    }
  };

  // 動画削除
  const handleDeleteVideo = async (index: number) => {
    const video = formData.videos[index];
    if (!video?.storagePath) return;

    try {
      await AdminClient.DeleteResultMedia({
        storagePath: video.storagePath,
      });
      setFormData(prev => ({
        ...prev,
        videos: prev.videos.filter((_, i) => i !== index),
      }));
    } catch (error) {
      console.error("動画の削除に失敗しました:", error);
      alert("動画の削除に失敗しました");
    }
  };

  // メディアURLを取得。/static/... のパスはそのまま nginx 経由、それ以外は Admin 画像プロキシ経由
  const getMediaUrl = (storagePath: string | undefined): string => {
    if (!storagePath) return "";
    return getImageUrl(storagePath) || "";
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "タイトルを入力してください";
    }

    if (!formData.categoryId) {
      newErrors.categoryId = "カテゴリを選択してください";
    }

    if (!formData.mainImage) {
      newErrors.mainImage = "メイン画像をアップロードしてください";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const resultId = formData.id || crypto.randomUUID();

      // 日付をTimestamp型に変換
      const completionDate = formData.completionDate
        ? TypeUtils.dateToTimestamp(new Date(formData.completionDate))
        : TypeUtils.dateToTimestamp(new Date());

      // 日付をDate型に変換するヘルパー
      const toDate = (date: any): Date => {
        if (!date) return new Date();
        if (date instanceof Date) return date;
        if (typeof date === "string") return new Date(date);
        if (date.seconds !== undefined) {
          const seconds =
            typeof date.seconds === "bigint"
              ? Number(date.seconds)
              : date.seconds;
          return new Date(seconds * 1000);
        }
        return new Date();
      };

      const submitData = {
        id: resultId,
        title: formData.title,
        categoryId: formData.categoryId,
        completionDate: completionDate,
        mainImage: formData.mainImage,
        galleryImages: formData.galleryImages,
        videos: formData.videos,
        clientName: formData.clientName,
        location: formData.location,
        siteArea: formData.siteArea,
        buildingArea: formData.buildingArea,
        totalFloorArea: formData.totalFloorArea,
        structureType: formData.structureType,
        description: formData.description,
        clientInfo: {
          companyName: formData.clientInfo.companyName,
          address: formData.clientInfo.address,
          websiteUrl: formData.clientInfo.websiteUrl,
          description: formData.clientInfo.description,
        },
        displayPriority: formData.displayPriority,
        isPublished: true,
        createDate: TypeUtils.dateToTimestamp(
          isEditMode ? toDate(formData.createDate) : new Date()
        ),
        updateDate: TypeUtils.dateToTimestamp(new Date()),
        version: isEditMode ? formData.version : "v1",
      };

      if (isEditMode) {
        await (AdminClient as any).UpdateResult({
          results: [submitData],
        });
        alert("施工実績を更新しました");
        navigate(`/admin/entities/result/${id}`);
      } else {
        await (AdminClient as any).CreateResult({
          results: [submitData],
        });
        alert("施工実績を作成しました");
        navigate("/admin/entities/result");
      }
    } catch (error) {
      console.error("保存に失敗しました:", error);
      alert(
        `保存に失敗しました: ${
          error instanceof Error ? error.message : "不明なエラー"
        }`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isEditMode && id) {
      navigate(`/admin/entities/result/${id}`);
    } else {
      navigate("/admin/entities/result");
    }
  };

  if (isLoading) {
    return (
      <div className="result-form-page">
        <div className="loading">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="result-form-page">
      <div className="result-form-container">
        <div className="result-form-header">
          <h1>{isEditMode ? "施工実績を編集" : "新しい施工実績を作成"}</h1>
          <p className="form-description">
            {isEditMode
              ? "施工実績の内容を編集できます"
              : "新しい施工実績を登録します"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="result-form">
          {/* 基本情報セクション */}
          <section className="form-section">
            <h2 className="section-title">基本情報</h2>

            {/* タイトル */}
            <div className="form-field">
              <label htmlFor="title" className="form-label">
                タイトル <span className="required">*</span>
              </label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                className={`form-input ${errors.title ? "error" : ""}`}
                placeholder="例：東洋産業株式会社『千葉支店 倉庫棟』新築工事（千葉市）"
              />
              {errors.title && (
                <div className="error-message">{errors.title}</div>
              )}
            </div>

            {/* カテゴリ */}
            <div className="form-field">
              <label htmlFor="categoryId" className="form-label">
                カテゴリ <span className="required">*</span>
              </label>
              <select
                id="categoryId"
                value={formData.categoryId}
                onChange={(e) => handleInputChange("categoryId", e.target.value)}
                className={`form-select ${errors.categoryId ? "error" : ""}`}
              >
                <option value="">カテゴリを選択</option>
                {categories
                  .filter((c) => c.isEnabled ?? c.is_enabled ?? true)
                  .map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
              </select>
              {errors.categoryId && (
                <div className="error-message">{errors.categoryId}</div>
              )}
            </div>

            {/* 表示優先度 */}
            <div className="form-field">
              <label htmlFor="displayPriority" className="form-label">
                表示優先度
              </label>
              <input
                type="number"
                id="displayPriority"
                value={formData.displayPriority}
                onChange={(e) =>
                  handleInputChange("displayPriority", Number(e.target.value))
                }
                className="form-input"
                min="0"
              />
              <p className="field-help">
                数値が大きいほど上に表示されます
              </p>
            </div>
          </section>

          {/* 工事概要セクション */}
          <section className="form-section">
            <h2 className="section-title">工事概要</h2>

            <div className="form-field">
              <label htmlFor="description" className="form-label">
                工事概要
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                className="form-textarea construction-overview"
                rows={12}
                placeholder={`例：
発注者名：東洋産業株式会社
敷地面積：1,234.56㎡
建築面積：987.65㎡
延床面積：2,345.67㎡
構造・階数：鉄骨造・２階

工事の詳細や特徴などを記載してください。`}
              />
              <p className="field-help">
                発注者名、面積、構造など工事の概要を自由に記載してください
              </p>
            </div>
          </section>

          {/* メディアセクション */}
          <section className="form-section">
            <h2 className="section-title">画像・動画</h2>

            {/* メイン画像 */}
            <div className="form-field">
              <label className="form-label">
                メイン画像 <span className="required">*</span>
              </label>
              <p className="field-help">サムネイルとして使用されます</p>

              {formData.mainImage ? (
                <div className="media-preview-container">
                  <div className="media-preview-item">
                    <img
                      src={getMediaUrl(formData.mainImage.storagePath)}
                      alt="メイン画像"
                      className="media-preview-image"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                    <div className="media-preview-info">
                      <span className="media-name">{formData.mainImage.name}</span>
                      <span className="media-size">
                        {Math.round((formData.mainImage.size || 0) / 1024)} KB
                      </span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={handleDeleteMainImage}
                    >
                      削除
                    </button>
                  </div>
                </div>
              ) : (
                <div className={`media-upload-area ${errors.mainImage ? "error" : ""}`}>
                  <input
                    ref={mainImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleMainImageUpload}
                    className="media-input"
                    disabled={uploadingMainImage}
                  />
                  {uploadingMainImage && (
                    <span className="uploading-indicator">アップロード中...</span>
                  )}
                </div>
              )}
              {errors.mainImage && (
                <div className="error-message">{errors.mainImage}</div>
              )}
            </div>

            {/* ギャラリー画像 */}
            <div className="form-field">
              <label className="form-label">ギャラリー画像</label>
              <p className="field-help">複数の画像をアップロードできます</p>

              <div className="media-upload-area">
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleGalleryUpload}
                  className="media-input"
                  disabled={uploadingGallery}
                />
                {uploadingGallery && (
                  <span className="uploading-indicator">アップロード中...</span>
                )}
              </div>

              {formData.galleryImages.length > 0 && (
                <div className="media-preview-container media-grid">
                  {formData.galleryImages.map((image, index) => (
                    <div key={index} className="media-preview-item">
                      <img
                        src={getMediaUrl(image.storagePath)}
                        alt={`ギャラリー ${index + 1}`}
                        className="media-preview-image"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                      <div className="media-preview-info">
                        <span className="media-name">{image.name}</span>
                        <span className="media-size">
                          {Math.round((image.size || 0) / 1024)} KB
                        </span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteGalleryImage(index)}
                      >
                        削除
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 動画 */}
            <div className="form-field">
              <label className="form-label">動画</label>
              <p className="field-help">複数の動画をアップロードできます（MP4, WebM, MOV）</p>

              <div className="media-upload-area">
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/*"
                  multiple
                  onChange={handleVideoUpload}
                  className="media-input"
                  disabled={uploadingVideo}
                />
                {uploadingVideo && (
                  <span className="uploading-indicator">アップロード中...</span>
                )}
              </div>

              {formData.videos.length > 0 && (
                <div className="media-preview-container">
                  {formData.videos.map((video, index) => (
                    <div key={index} className="media-preview-item video-item">
                      <video
                        src={getMediaUrl(video.storagePath)}
                        className="media-preview-video"
                        controls
                      />
                      <div className="media-preview-info">
                        <span className="media-name">{video.name}</span>
                        <span className="media-size">
                          {Math.round((video.size || 0) / 1024 / 1024 * 10) / 10} MB
                        </span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteVideo(index)}
                      >
                        削除
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* 発注企業情報セクション */}
          <section className="form-section">
            <h2 className="section-title">発注企業情報（任意）</h2>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="clientCompanyName" className="form-label">
                  企業名
                </label>
                <input
                  type="text"
                  id="clientCompanyName"
                  value={formData.clientInfo.companyName}
                  onChange={(e) =>
                    handleClientInfoChange("companyName", e.target.value)
                  }
                  className="form-input"
                />
              </div>

              <div className="form-field">
                <label htmlFor="clientAddress" className="form-label">
                  住所
                </label>
                <input
                  type="text"
                  id="clientAddress"
                  value={formData.clientInfo.address}
                  onChange={(e) =>
                    handleClientInfoChange("address", e.target.value)
                  }
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="clientWebsiteUrl" className="form-label">
                ウェブサイトURL
              </label>
              <input
                type="url"
                id="clientWebsiteUrl"
                value={formData.clientInfo.websiteUrl}
                onChange={(e) =>
                  handleClientInfoChange("websiteUrl", e.target.value)
                }
                className="form-input"
                placeholder="https://example.com"
              />
            </div>

            <div className="form-field">
              <label htmlFor="clientDescription" className="form-label">
                企業説明
              </label>
              <textarea
                id="clientDescription"
                value={formData.clientInfo.description}
                onChange={(e) =>
                  handleClientInfoChange("description", e.target.value)
                }
                className="form-textarea"
                rows={3}
              />
            </div>
          </section>

          {/* ボタン */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting || uploadingMainImage || uploadingGallery || uploadingVideo}
            >
              {isSubmitting
                ? "保存中..."
                : isEditMode
                ? "更新する"
                : "作成する"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
